import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCache } from '../context/CacheContext';
import Layout from '../components/Layout';
import '../styles/Dashboard.css';
import { cachedDashboardService } from '../services/cachedApiService';

// Icons
const DashboardIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 5C4 4.44772 4.44772 4 5 4H9C9.55228 4 10 4.44772 10 5V9C10 9.55228 9.55228 10 9 10H5C4.44772 10 4 9.55228 4 9V5Z" fill="currentColor" />
    <path d="M14 5C14 4.44772 14.4477 4 15 4H19C19.5523 4 20 4.44772 20 5V9C20 9.55228 19.5523 10 19 10H15C14.4477 10 14 9.55228 14 9V5Z" fill="currentColor" />
    <path d="M4 15C4 14.4477 4.44772 14 5 14H9C9.55228 14 10 14.4477 10 15V19C10 19.5523 9.55228 20 9 20H5C4.44772 20 4 19.5523 4 19V15Z" fill="currentColor" />
    <path d="M14 15C14 14.4477 14.4477 14 15 14H19C19.5523 14 20 14.4477 20 15V19C20 19.5523 19.5523 20 19 20H15C14.4477 20 14 19.5523 14 19V15Z" fill="currentColor" />
  </svg>
);

const PatientIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" fill="currentColor" />
    <path d="M12 13C7.58172 13 4 16.5817 4 21H20C20 16.5817 16.4183 13 12 13Z" fill="currentColor" />
  </svg>
);

const MedicalIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 2V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M16 2V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M3 8H21" stroke="currentColor" strokeWidth="2" />
    <path d="M4 4H20C20.5523 4 21 4.44772 21 5V20C21 20.5523 20.5523 21 20 21H4C3.44772 21 3 20.5523 3 20V5C3 4.44772 3.44772 4 4 4Z" stroke="currentColor" strokeWidth="2" />
    <path d="M12 10V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M9 13H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const DocumentIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 3V7C14 7.55228 14.4477 8 15 8H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H14L19 8V19C19 20.1046 18.1046 21 17 21Z" stroke="currentColor" strokeWidth="2" />
    <path d="M9 13H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M9 17H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const ChartIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 13V17M12 9V17M16 5V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21Z" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { invalidate } = useCache();
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboardStats = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        const data = await cachedDashboardService.getDashboardStats();
        setStats(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching dashboard stats:', err);
        setError('Failed to load dashboard data');
        setLoading(false);
      }
    };

    fetchDashboardStats();
  }, [user]);

  if (!user || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="p-8 bg-white rounded-lg shadow-md">
          <div className="flex items-center justify-center">
            <div className="w-12 h-12 border-t-4 border-b-4 border-blue-500 rounded-full animate-spin"></div>
          </div>
          <p className="mt-4 text-center text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="error-box">
          <h2>Error Loading Dashboard</h2>
          <p>{error}</p>
          <button onClick={() => {
            invalidate.dashboard();
            window.location.reload();
          }} className="btn">Retry</button>
        </div>
      </Layout>
    );
  }

  // Get current date for greeting
  const currentHour = new Date().getHours();
  let greeting = "Good Morning";
  if (currentHour >= 12 && currentHour < 17) {
    greeting = "Good afternoon";
  } else if (currentHour >= 17) {
    greeting = "Good Evening";
  }

  return (
    <Layout>
      <div className="welcome-box">
        <h1>{greeting}, {user.fname ? `${user.fname} ${user.lname || ''}` : user.username}!</h1>
        <p>Welcome to your Prior Authorization dashboard. You are logged in as <strong>{user.role}</strong>.</p>
      </div>

      {user.role === 'patient' && (
        <>
          <div className="features-box">
            <h2><PatientIcon /> Patient Portal</h2>
            <ul>
              <li><a href={`/patients/${user.id}`}><MedicalIcon /> View Your Medical Records</a></li>
              <li><a href={`/prior-authorizations/${user.id}`}><DocumentIcon /> View Your Prior Authorizations</a></li>
              {/* <li><a href="#"><DocumentIcon /> Download Medical Reports</a></li> */}
            </ul>
            <div className="id-box">
              <div>Patient ID: <strong>{user.id}</strong></div>
              <div>Patient Name: <strong>{user.fname ? `${user.fname} ${user.lname || ''}` : user.username}</strong></div>
            </div>
          </div>

          <div className="dashboard-grid">
            <div className="stats-card">
              <div className="stats-card-title">Active Prescriptions</div>
              <div className="stats-card-value">{stats?.active_prescriptions || 0}</div>
              <div className="stats-card-change">Last updated: Today</div>
            </div>
            <div className="stats-card">
              <div className="stats-card-title">Prior Authorizations</div>
              <div className="stats-card-value">{stats?.prior_authorizations || 0}</div>
              <div className="stats-card-change">
                {stats?.pending_prior_auths > 0
                  ? `${stats.pending_prior_auths} pending`
                  : 'All completed'}
              </div>
            </div>
            <div className="stats-card">
              <div className="stats-card-title">Latest PA Status</div>
              <div className="stats-card-value">{stats?.latest_pa_status || 'None'}</div>
              <div className="stats-card-change">
                {stats?.latest_pa_date !== 'N/A'
                  ? `Created: ${stats?.latest_pa_date}`
                  : 'No prior authorizations'}
              </div>
            </div>
          </div>
        </>
      )}

      {user.role === 'provider' && (
        <>
          <div className="features-box">
            <h2><MedicalIcon /> Provider Dashboard</h2>
            <ul>
              <li><a href="/patients"><PatientIcon /> View Your Patients</a></li>
              <li><a href="/provider/pa-status"><DocumentIcon /> View Prior Authorization Status</a></li>
              {/* <li><a href="#"><MedicalIcon /> Manage Appointments</a></li> */}
              {/* <li><a href="#"><DocumentIcon /> Create Medical Reports</a></li> */}
            </ul>
            <div className="id-box">
              <div>Provider ID: <strong>{user.id}</strong></div>
              <div>Provider Name: <strong>{user.fname ? `${user.fname} ${user.lname || ''}` : user.username}</strong></div>
            </div>
          </div>

          <div className="dashboard-grid">
            <div className="stats-card">
              <div className="stats-card-title">Total Patients</div>
              <div className="stats-card-value">{stats?.total_patients || 0}</div>
              <div className="stats-card-change positive">Under your care</div>
            </div>
            <div className="stats-card">
              <div className="stats-card-title">Pending PA Requests</div>
              <div className="stats-card-value">{stats?.pending_pa_requests || 0}</div>
              <div className="stats-card-change">Needs attention</div>
            </div>
            <div className="stats-card">
              <div className="stats-card-title">Total PA Requests</div>
              <div className="stats-card-value">{stats?.total_pa_requests || 0}</div>
              <div className="stats-card-change">For your patients</div>
            </div>
          </div>
        </>
      )}

      {user.role === 'superuser' && (
        <>
          <div className="features-box">
            <h2><DashboardIcon /> Admin Control Panel</h2>
            <ul>
              <li><a href="/patients"><PatientIcon /> View All Patients</a></li>
              <li><a href="/practitioners"><MedicalIcon /> View All Providers</a></li>
              <li><a href="/prior-authorizations"><DocumentIcon /> Manage PA Requests</a></li>
              <li><a href="/admin-overview"><ChartIcon /> System Overview</a></li>
            </ul>
            <div className="id-box">
              <div>Admin ID: <strong>{user.id}</strong></div>
              <div>Admin Name: <strong>{user.fname ? `${user.fname} ${user.lname || ''}` : user.username}</strong></div>
              <div>Role: <strong>System Administrator</strong></div>
            </div>
          </div>

          <div className="dashboard-grid">
            <div className="stats-card">
              <div className="stats-card-title">Total Patients</div>
              <div className="stats-card-value">{stats?.total_patients || 0}</div>
              <div className="stats-card-change positive">+{stats?.new_patients_this_month || 0} this month</div>
            </div>
            <div className="stats-card">
              <div className="stats-card-title">Total Providers</div>
              <div className="stats-card-value">{stats?.total_providers || 0}</div>
              <div className="stats-card-change positive">+{stats?.new_providers_this_month || 0} this month</div>
            </div>
            <div className="stats-card">
              <div className="stats-card-title">PA Requests</div>
              <div className="stats-card-value">{stats?.total_pa_requests || 0}</div>
              <div className="stats-card-change">{stats?.pending_pa_requests || 0} pending approval</div>
            </div>
            <div className="stats-card">
              <div className="stats-card-title">Total Encounters</div>
              <div className="stats-card-value">{stats?.total_encounters || 0}</div>
              <div className="stats-card-change">Patient visits recorded</div>
            </div>
            <div className="stats-card">
              <div className="stats-card-title">System Status</div>
              <div className="stats-card-value">{stats?.system_status || "Unknown"}</div>
              <div className="stats-card-change positive">{stats?.system_details || "Status unavailable"}</div>
            </div>
          </div>
        </>
      )}

      {user.role === 'payer' && (
        <>
          <div className="features-box">
            <h2><DocumentIcon /> Payer Dashboard</h2>
            <ul>
              <li><a href="/payer/pending-authorizations"><DocumentIcon /> Pending Authorizations</a></li>
              <li><a href="/payer/reviewed-authorizations"><DocumentIcon /> Reviewed Authorizations</a></li>
              <li><a href="/payer/epa-management"><ChartIcon /> ePA Management</a></li>
            </ul>
            <div className="id-box">
              <div>Payer ID: <strong>{user.id}</strong></div>
              <div>Payer Name: <strong>{user.fname ? `${user.fname} ${user.lname || ''}` : user.username}</strong></div>
              <div>Role: <strong>Insurance Payer</strong></div>
            </div>
          </div>

          <div className="dashboard-grid">
            <div className="stats-card">
              <div className="stats-card-title">Pending Authorizations</div>
              <div className="stats-card-value">{stats?.pending_pa_requests || 0}</div>
              <div className="stats-card-change">Awaiting your review</div>
            </div>
            <div className="stats-card">
              <div className="stats-card-title">Approved Authorizations</div>
              <div className="stats-card-value">{stats?.approved_pa_requests || 0}</div>
              <div className="stats-card-change positive">Last 30 days</div>
            </div>
            <div className="stats-card">
              <div className="stats-card-title">Rejected Authorizations</div>
              <div className="stats-card-value">{stats?.rejected_pa_requests || 0}</div>
              <div className="stats-card-change">Last 30 days</div>
            </div>
            <div className="stats-card">
              <div className="stats-card-title">Average Review Time</div>
              <div className="stats-card-value">{stats?.avg_review_time || "N/A"}</div>
              <div className="stats-card-change">Hours per request</div>
            </div>
            <div className="stats-card">
              <div className="stats-card-title">ePA Status</div>
              <div className="stats-card-value">{stats?.epa_enabled ? "Enabled" : "Disabled"}</div>
              <div className="stats-card-change">{stats?.epa_details || "No details available"}</div>
            </div>
          </div>
        </>
      )}
    </Layout>
  );
};

export default Dashboard;